<template>
  <div class="analytics-insights">
    <section aria-label="Топ товаров по прибыли">
      <data-table
        v-if="profit"
        :rows="profit.rows"
        :columns="profitColumns"
        :search-keys="['title']"
        title="Топ товаров по прибыли"
        description="Оценочная валовая прибыль за выбранный период"
        search-placeholder="Название товара"
        empty-text="Нет данных о прибыли по товарам за выбранный период"
      >
        <template #cell-title="{ row }">{{ productTitle(row) }}</template>
        <template
          v-for="column in profitMetrics"
          v-slot:[`cell-${column.key}`]="{ row }"
        >
          <span
            :key="column.key"
            :class="{ 'analytics-insights__negative': row[column.key] < 0 }"
          >
            {{ formatMetric(row[column.key], column.unit) }}
          </span>
        </template>
      </data-table>


      <div v-else class="analytics-insights__card">
        <h2>Топ товаров по прибыли</h2>
        <p role="status">Блок недоступен: сервер не передал данные о прибыли по товарам.</p>
      </div>

      <p class="analytics-insights__hint">
        Себестоимость чека распределена между товарами пропорционально ценам позиций.
        Это оценка валовой прибыли до выплат продавцам и других расходов.
      </p>

      <p class="analytics-insights__hint">
        Используются исторические суммы чеков, а не текущие закупочные цены. Возвраты
        исключены; повторения товара в чеке считаются отдельными единицами. Сумма топа может отличаться от общей прибыли
        из-за ограничения количества товаров и пропущенных чеков.
      </p>

      <p
        v-if="profit && profit.equalAllocationReceipts > 0"
        class="analytics-insights__hint"
      >
        Чеков с равным распределением сумм между единицами товара: {{ formatMetric(profit.equalAllocationReceipts) }}.
        Цены позиций отсутствуют, повреждены или не согласуются с составом чека.
      </p>

      <p
        v-if="profit && profit.skippedReceipts > 0"
        class="analytics-insights__hint"
      >
        Пропущено чеков без идентификаторов товаров: {{ formatMetric(profit.skippedReceipts) }}.
      </p>
    </section>

    <section aria-label="Покупают вместе">
      <data-table
        v-if="together"
        :rows="pairRows"
        :columns="pairColumns"
        row-key="pairKey"
        :search-keys="['firstTitle', 'secondTitle']"
        title="Покупают вместе"
        description="Пары товаров по частоте совместных покупок"
        search-placeholder="Название одного из товаров"
        empty-text="Совместных покупок за выбранный период нет"
      >
        <template #cell-receipts="{ row }">{{ formatMetric(row.receipts) }}</template>
        <template #cell-receiptSharePercent="{ row }">{{ formatMetric(row.receiptSharePercent, 'percent') }}</template>
      </data-table>

      <div v-else class="analytics-insights__card">
        <h2>Покупают вместе</h2>
        <p role="status">Блок недоступен: сервер не передал данные о совместных покупках.</p>
      </div>

      <p
        v-if="together"
        class="analytics-insights__hint"
      >
        Всего невозвращённых чеков за выбранный период и сотрудника: {{ formatMetric(together.totalReceipts) }}.
      </p>

      <p class="analytics-insights__hint">
        Доля рассчитана от всех невозвращённых чеков, включая покупки одного товара.
        Это доля чеков, а не вероятность покупки второго товара. Каждая пара разных ID учитывается один раз в чеке;
        одинаковые названия не объединяются.
      </p>
    </section>

    <section
      class="analytics-insights__card"
      aria-label="Прогноз на конец месяца"
    >
      <h2>{{ forecastTitle }}</h2>

      <p
        v-if="!forecast"
        role="status"
      >
        Блок недоступен: сервер не передал прогноз на конец месяца.
      </p>

      <template v-else>
        <p class="analytics-insights__hint">
          Период блока: {{ forecast.from }} — {{ forecast.to }} (UTC). Весь месяц
          конечной даты фильтра, независимо от выбранной начальной даты и группировки графиков.
          Фильтр сотрудника учитывается.
        </p>

        <p role="status">{{ forecastStatus }}</p>

        <p
          v-if="forecast.observedDays > 0"
          class="analytics-insights__hint"
        >
          По данным за {{ forecast.observedDays }} завершённых дней, по {{ forecast.observedThrough }} включительно (UTC).
        </p>

        <div class="analytics-insights__metrics">
          <article
            v-for="metric in forecastMetrics"
            :key="metric.key"
          >
            <h3>{{ metric.label }}</h3>

            <p>{{ forecast.status === 'completed' ? 'Итог месяца' : 'Факт за завершённые дни' }}</p>

            <strong
              :class="{ 'analytics-insights__negative': forecast.actual && forecast.actual[metric.key] < 0 }"
            >
              {{ formatMetric(forecast.actual && forecast.actual[metric.key], metric.unit) }}
            </strong>

            <template v-if="forecast.status === 'forecast'">
              <p>Ожидаемый итог всего месяца</p>
              <strong
                :class="{ 'analytics-insights__negative': forecast.projected && forecast.projected[metric.key] < 0 }"
              >
                {{ projectedValue(metric) }}
              </strong>
            </template>
          </article>
        </div>

        <p class="analytics-insights__hint">
          Линейная оценка: факт / число завершённых дней × число дней в месяце.
          Включены дни без продаж; текущий неполный день исключён. Сезонность и будущие возвраты не учитываются. Валовая
          прибыль указана до выплат продавцам, аренды и других расходов.
        </p>
      </template>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import DataTable from '@/components/dataTable'
import { DATE_FORMAT, formatMetric } from '../analytics'

const profitMetrics = [
  {
    key: 'units',
    label: 'Продано единиц',
    unit: 'number'
  },
  {
    key: 'revenue',
    label: 'Выручка',
    unit: 'money'
  },
  {
    key: 'estimatedCost',
    label: 'Расчётная себестоимость',
    unit: 'money'
  },
  {
    key: 'estimatedGrossProfit',
    label: 'Расчётная валовая прибыль',
    unit: 'money'
  },
  {
    key: 'marginPercent',
    label: 'Маржинальность',
    unit: 'percent'
  }
]

export default {
  name: 'SalesInsights',
  components: { DataTable },
  props: {
    report: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      profitMetrics,
      profitColumns: [
        {
          key: 'title',
          label: 'Товар'
        },
        ...profitMetrics
      ].map(column => ({...column, sortable: false})),
      pairColumns: [
        {
          key: 'firstTitle',
          label: 'Первый товар',
          sortable: false
        },

        { key: 'secondTitle',
          label: 'Второй товар',
          sortable: false
        },

        { key: 'receipts',
          label: 'Совместных чеков',
          sortable: false
        },

        { key: 'receiptSharePercent',
          label: 'Доля всех чеков',
          sortable: false
        }
      ],
      forecastMetrics: [
        {
          key: 'revenue',
          label: 'Выручка',
          unit: 'money'
        },
        {
          key: 'grossProfit',
          label: 'Валовая прибыль',
          unit: 'money'
        },
        {
          key: 'receipts',
          label: 'Число продаж (чеков)',
          unit: 'number'
        }
      ]
    }
  },
  computed: {
    profit () {
      return Array.isArray(this.report.topProductsByProfit?.rows) ? this.report.topProductsByProfit : null
    },
    together () {
      return Array.isArray(this.report.frequentlyBoughtTogether?.rows) ? this.report.frequentlyBoughtTogether : null
    },
    pairRows () {
      return (this.together?.rows || []).map(row => ({
        ...row,
        pairKey: JSON.stringify(row.products.map(product => product.id).sort()),
        firstTitle: this.productTitle(row.products[0]),
        secondTitle: this.productTitle(row.products[1])
      }))
    },
    forecast () {
      return this.report.monthForecast || null
    },
    forecastTitle () {
      const month = moment(this.forecast?.from, DATE_FORMAT, true)
      const title = this.forecast?.status === 'completed' ? 'Итоги месяца' : 'Прогноз на конец месяца'
      return month.isValid() ? `${title} — ${month.locale('ru').format('MMMM YYYY')}` : title
    },
    forecastStatus () {
      return {
        forecast: 'Оценка по среднему дневному темпу',
        completed: 'Месяц завершён. Показаны фактические итоги всего месяца.',
        insufficient_data: 'Прогноз появится после первого завершённого дня месяца',
        future: 'Прогноз для будущего месяца пока недоступен'
      }[this.forecast?.status] || 'Прогноз недоступен'
    }
  },
  methods: {
    formatMetric,
    productTitle(product) {
      return product.title || `Товар ${product.id}`
    },
    projectedValue(metric) {
      const value = this.forecast.projected?.[metric.key]

      if (metric.key === 'receipts' && Number.isFinite(value) && !Number.isInteger(value)) {
        return `≈ ${formatMetric(Math.round(value))}`
      }
      return formatMetric(value, metric.unit)
    }
  }
}
</script>

<style scoped lang="scss">
.analytics-insights {
  min-width: 0;

  section {
    margin: 20px 0;
  }

  &__card {
    background: #fff;
    padding: 20px;
    border: 1px solid #e2e8f0;
    border-radius: 14px;

    h2 {
      margin: 0 0 10px;
      font-size: var(--font-size-md);
      font-weight: 600;
    }
  }

  &__hint {
    color: #788598;
    font-size: var(--font-size-sm);
    line-height: 1.65;
    margin: 12px 0;
  }

  &__metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;

    article {
      padding: 16px;
      background: #fafcff;
      border-radius: 12px;
      min-width: 0;
    }

    h3 {
      margin: 0;
      font-size: var(--font-size-base);
    }

    p {
      color: #687990;
      font-size: var(--font-size-sm);
    }

    strong {
      font-size: var(--font-size-xl);
      overflow-wrap: anywhere;
    }
  }

  &__negative {
    color: #b83c49;
  }

  @media (max-width: 600px) {
    &__metrics {
      grid-template-columns: 1fr;
    }
    &__card {
      padding: 16px;
    }
  }
}
</style>
